import React from 'react';
import { Link } from 'react-router-dom';
import Breadcrumb from '../components/Breadcrumb';
import AnalogyCard from '../components/AnalogyCard';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { AlertTriangle, TrendingUp, DollarSign, Lock, HelpCircle } from 'lucide-react';
import { ccAnalogies } from '../mock';
import SEO, { generateArticleSchema, generateBreadcrumbSchema, generateFAQSchema } from '../components/SEO';
import AdSense from '../components/AdSense';

const CoveredCalls = () => {
  return (
    <div className="min-h-screen">
      <SEO
        title="Covered Call Explained for Beginners (Analogies, Examples & Risks)"
        description="Learn covered calls in plain English. Real-world analogies like renting out your shares. Risks, capped upside, and when to use. Options for beginners."
        keywords="covered call explained, covered call for beginners, covered call analogy, how do covered calls work, renting out shares, covered call example, covered call strategy explained simply"
        canonical="https://optionsanalogies.com/covered-calls"
        type="article"
        article={true}
        publishedTime="2025-01-15T00:00:00Z"
        modifiedTime="2025-01-15T00:00:00Z"
        schema={[
          generateArticleSchema({
            title: "Covered Call Explained for Beginners (Analogies, Examples & Risks)",
            description: "Learn covered calls in plain English with real-world analogies and examples. Understand risks and capped upside.",
            url: "https://optionsanalogies.com/covered-calls",
            datePublished: "2025-01-15T00:00:00Z",
            dateModified: "2025-01-15T00:00:00Z",
            image: "https://optionsanalogies.com/og-cc.png"
          }),
          generateBreadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Covered Calls", path: "/covered-calls" }
          ]),
          generateFAQSchema([
            {
              question: "What is a covered call in simple terms?",
              answer: "A covered call is like getting paid NOW to agree to sell a stock you own LATER at a specific price (the strike price). You collect premium upfront, but you give up gains above the strike price. You must own 100 shares per contract to 'cover' your obligation."
            },
            {
              question: "Why do covered calls cap your upside?",
              answer: "When you sell a covered call, you give the buyer the right to purchase your shares at the strike price. If the stock rises above the strike, the buyer will exercise and your shares get 'called away' at the strike price. You miss all gains above that level, though you keep the premium."
            },
            {
              question: "Can I lose money with covered calls?",
              answer: "Yes. While the premium provides a small buffer, you still face downside risk. If the stock drops significantly, your loss on the shares will exceed the premium collected. Covered calls reduce your cost basis but don't eliminate downside risk."
            },
            {
              question: "What is the covered call analogy?",
              answer: "Think of it like renting out your house. You collect rent (premium) for agreeing to sell your house (shares) at a set price (strike) if the buyer exercises. You get income now, but you give up big gains if the house value skyrockets."
            },
            {
              question: "Is covered call strategy safe for beginners?",
              answer: "Covered calls are considered conservative if you already own the stock and understand the trade-offs. However, they still carry risk. The stock can drop, and you'll miss out on large upward moves. Not suitable if you expect the stock to rally significantly."
            }
          ])
        ]}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Breadcrumb items={[{ label: 'Covered Calls', path: '/covered-calls' }]} />

        {/* Header - Ad-Free Zone */}
        <div className="mb-12" data-nosnippet>
          <div className="mb-4">
            <span className="text-sm text-gray-400">Also known as:</span>
            <p className="text-gray-300">buy-write, covered call write, overwrite, covered writing</p>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Covered Call (CC)
          </h1>
          <p className="text-2xl text-emerald-500 font-medium">
            "A paid listing agreement: you're paid now to agree you'll sell at your price."
          </p>
        </div>

        {/* Core Mechanics - Ad-Free Zone */}
        <section className="mb-16" data-nosnippet>
          <h2 className="text-3xl font-bold text-white mb-8">Core Mechanics</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="bg-gray-900/50 border-gray-800">
              <CardHeader>
                <Lock className="w-8 h-8 text-emerald-500 mb-2" />
                <CardTitle className="text-white">You Own Shares</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-400">
                  Must own 100 shares per contract. The shares "cover" your obligation.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-gray-900/50 border-gray-800">
              <CardHeader>
                <DollarSign className="w-8 h-8 text-emerald-500 mb-2" />
                <CardTitle className="text-white">You Sell a Call</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-400">
                  Collect premium upfront and grant the buyer the right to purchase your shares at the strike.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-gray-900/50 border-gray-800">
              <CardHeader>
                <TrendingUp className="w-8 h-8 text-emerald-500 mb-2" />
                <CardTitle className="text-white">If Stock > Strike at Expiration</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-400">
                  Buyer exercises → you sell your shares at strike price (called away).
                </p>
              </CardContent>
            </Card>

            <Card className="bg-gray-900/50 border-gray-800">
              <CardHeader>
                <TrendingUp className="w-8 h-8 text-gray-500 mb-2" />
                <CardTitle className="text-white">If Stock ≤ Strike</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-400">
                  Option expires worthless; you keep premium + shares. Can sell another call.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Math Tile */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-8">Math (Per Share)</h2>
          <div className="glass rounded-xl p-8">
            <div className="grid md:grid-cols-3 gap-8">
              <div>
                <h3 className="text-emerald-400 font-semibold mb-2">Max Profit (Capped)</h3>
                <p className="text-2xl text-white font-mono">≈ (Strike − Cost) + Premium</p>
                <p className="text-sm text-gray-400 mt-2">Capital gains + premium collected</p>
              </div>
              <div>
                <h3 className="text-emerald-400 font-semibold mb-2">Downside Buffer</h3>
                <p className="text-2xl text-white font-mono">= Premium</p>
                <p className="text-sm text-gray-400 mt-2">Reduces your cost basis</p>
              </div>
              <div>
                <h3 className="text-red-400 font-semibold mb-2">Max Loss</h3>
                <p className="text-2xl text-white font-mono">≈ Cost Basis − Premium</p>
                <p className="text-sm text-gray-400 mt-2">If stock goes to $0 (unlikely)</p>
              </div>
            </div>
            <div className="mt-8 pt-8 border-t border-gray-700">
              <h3 className="text-emerald-400 font-semibold mb-2">New Effective Basis</h3>
              <p className="text-2xl text-white font-mono">= Original Cost − Premium</p>
              <p className="text-sm text-gray-400 mt-2">Premium reduces your holding cost</p>
            </div>
          </div>
        </section>

        {/* Risk Callouts */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-8">Risk Callouts</h2>
          <div className="space-y-4">
            <Alert className="border-red-500/50 bg-red-500/10">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              <AlertDescription className="text-gray-300 ml-2">
                <strong className="text-white">Opportunity Cost:</strong> If the stock "moons" (rallies significantly), you're capped at the strike price. You miss all upside above strike + premium.
              </AlertDescription>
            </Alert>
            <Alert className="border-red-500/50 bg-red-500/10">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              <AlertDescription className="text-gray-300 ml-2">
                <strong className="text-white">Downside Exposure:</strong> You still bear most downside risk (just cushioned by premium). If stock drops $10 and you collected $1, you're down $9.
              </AlertDescription>
            </Alert>
          </div>
        </section>

        {/* Ad Placement 1 - After Math */}
        <section className="mb-16">
          <AdSense slot="5123032414" format="horizontal" />
        </section>

        {/* CC Analogies */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-8">Covered Call Analogies</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {ccAnalogies.map((analogy) => (
              <AnalogyCard key={analogy.id} analogy={analogy} />
            ))}
          </div>
        </section>

        {/* Ad Placement 2 - After Analogies */}
        <section className="mb-16">
          <AdSense slot="2827315214" format="rectangle" />
        </section>

        {/* When to Use */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-8">When to Use Covered Calls</h2>
          <Card className="bg-gray-900/50 border-gray-800">
            <CardContent className="pt-6">
              <ul className="space-y-4">
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-emerald-500 rounded-full mt-2 flex-shrink-0" />
                  <p className="text-gray-300">
                    <strong className="text-white">Neutral to mildly bullish:</strong> You believe the stock will trade sideways or move up modestly, not explosively.
                  </p>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-emerald-500 rounded-full mt-2 flex-shrink-0" />
                  <p className="text-gray-300">
                    <strong className="text-white">Happy to sell at target:</strong> You've identified a price where you're genuinely comfortable taking profit.
                  </p>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-emerald-500 rounded-full mt-2 flex-shrink-0" />
                  <p className="text-gray-300">
                    <strong className="text-white">Income generation:</strong> You seek regular premium income and a soft downside buffer on existing holdings.
                  </p>
                </li>
              </ul>
            </CardContent>
          </Card>
        </section>

        {/* See Also - Internal Linking */}
        <section className="mb-16">
          <div className="glass rounded-xl p-8">
            <h2 className="text-2xl font-bold text-white mb-6">Related Strategies</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="bg-gray-900/50 border-gray-800 hover:border-emerald-500 transition">
                <CardHeader>
                  <CardTitle className="text-emerald-400">Cash-Secured Puts</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-300 mb-4">
                    Before owning shares, learn how <Link to="/csp" className="text-emerald-400 hover:text-emerald-300 underline">cash-secured puts</Link> let you get paid to enter a position at your target price.
                  </p>
                </CardContent>
              </Card>
              <Card className="bg-gray-900/50 border-gray-800 hover:border-emerald-500 transition">
                <CardHeader>
                  <CardTitle className="text-emerald-400">The Wheel Strategy</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-300 mb-4">
                    See how <Link to="/wheel" className="text-emerald-400 hover:text-emerald-300 underline">the Wheel strategy</Link> combines cash-secured puts and covered calls into a repeatable income cycle.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Beginner FAQs */}
        <section className="mb-16">
          <div className="glass rounded-xl p-8">
            <div className="flex items-center gap-3 mb-8">
              <HelpCircle className="w-8 h-8 text-emerald-500" />
              <h2 className="text-3xl font-bold text-white">Frequently Asked Questions</h2>
            </div>
            
            <div className="space-y-6">
              <div className="border-b border-gray-700 pb-6">
                <h3 className="text-xl font-semibold text-white mb-3">What is a covered call in simple terms?</h3>
                <p className="text-gray-300 leading-relaxed">
                  A covered call is like getting paid NOW to agree to sell a stock you own LATER at a specific price (the strike price). You collect premium upfront, but you give up gains above the strike price. You must own 100 shares per contract to "cover" your obligation.
                </p>
              </div>

              <div className="border-b border-gray-700 pb-6">
                <h3 className="text-xl font-semibold text-white mb-3">Why do covered calls cap your upside?</h3>
                <p className="text-gray-300 leading-relaxed">
                  When you sell a covered call, you give the buyer the right to purchase your shares at the strike price. If the stock rises above the strike, the buyer will exercise and your shares get "called away" at the strike price. You miss all gains above that level, though you keep the premium.
                </p>
              </div>

              <div className="border-b border-gray-700 pb-6">
                <h3 className="text-xl font-semibold text-white mb-3">Can I lose money with covered calls?</h3>
                <p className="text-gray-300 leading-relaxed">
                  Yes. While the premium provides a small buffer, you still face downside risk. If the stock drops significantly, your loss on the shares will exceed the premium collected. Covered calls reduce your cost basis but don't eliminate downside risk.
                </p>
              </div>

              <div className="border-b border-gray-700 pb-6">
                <h3 className="text-xl font-semibold text-white mb-3">What is the covered call analogy?</h3>
                <p className="text-gray-300 leading-relaxed">
                  Think of it like renting out your house. You collect rent (premium) for agreeing to sell your house (shares) at a set price (strike) if the buyer exercises. You get income now, but you give up big gains if the house value skyrockets.
                </p>
              </div>

              <div className="pb-6">
                <h3 className="text-xl font-semibold text-white mb-3">Is covered call strategy safe for beginners?</h3>
                <p className="text-gray-300 leading-relaxed">
                  Covered calls are considered conservative if you already own the stock and understand the trade-offs. However, they still carry risk. The stock can drop, and you'll miss out on large upward moves. Not suitable if you expect the stock to rally significantly.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Enhanced Disclaimer */}
        <section className="mb-16">
          <Alert className="border-amber-500/50 bg-amber-500/10">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            <AlertDescription className="text-gray-300 ml-2">
              <strong className="text-white">Education Only - Not Financial Advice:</strong> This content is for educational purposes only and does not constitute financial, tax, or legal advice. Options trading involves significant risk and may not be suitable for all investors. You could lose your entire investment. Before trading options, carefully consider your financial situation, investment objectives, and risk tolerance. Consult with a qualified financial advisor before making any investment decisions.
            </AlertDescription>
          </Alert>
        </section>
      </div>
    </div>
  );
};

export default CoveredCalls;